// MyQNATest.jsx
import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import faqStyle from "./FaqStyle.module.css";
import {
  FaPlus,
  FaPen,
  FaCommentDots,
  FaTrash,
  FaInfoCircle,
  FaEdit,
  FaCopy,
  FaEye,
  FaArrowUp,
  FaArrowDown,
  FaSearch,
  FaImage,
  FaChevronDown,
  FaChevronRight,
  FaBolt,
} from "react-icons/fa";
import {
  MdOutlineAddToPhotos,
  MdOutlineWidgets,
  MdOutlineClose,
} from "react-icons/md";
import { RiDeleteBin6Line } from "react-icons/ri";
import { BiSolidEdit } from "react-icons/bi";
import { IoClose } from "react-icons/io5";

import SpecialRichEditor from "../NewCreateAppF/SpecialRichEditor";

import {
  resolveFieldComponent,
  ComponentPickerModal,
  PinConfirmModal,
} from "../SideData/Overview/AiAgentWorkFlow/utils/uiFieldComponentsShared";

import ValidationRulesBuilder from "../SideData/Overview/AiAgentWorkFlow/ValidationRulesBuilder";
import {
  fetchRulesForForm,
  saveRulesForForm,
} from "../SideData/Overview/AiAgentWorkFlow/utils/validationRulesApi";

import TriggerAttachFlow, {
  TriggerAttachmentList,
} from "../SideData/Overview/AiAgentWorkFlow/TriggerAttachFlow";
import { siteFieldsFromFormNode } from "../SideData/Overview/AiAgentWorkFlow/siteFields";
import { normaliseTriggerList } from "../SideData/Overview/AiAgentWorkFlow/triggerAttachment";
import { listOperations } from "../SideData/Overview/AiAgentWorkFlow/utils/operationsApi";
import { listDefinitions } from "../SideData/Overview/AiAgentWorkFlow/utils/connectorDefinitionsApi";
import { listConnections } from "../SideData/Overview/AiAgentWorkFlow/utils/oauthApi";

// ⭐ Font Awesome Icon Picker Modal
const FontAwesomeIconPicker = ({ onSelect, onClose }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await fetch("https://api.fontawesome.com", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            query: `
              query SearchIcons($query: String!) {
                searchPaginated(version: "6.x", query: $query, pageSize: 30) {
                  icons {
                    id
                    label
                  }
                }
              }
            `,
            variables: { query: query },
          }),
        });
        const data = await response.json();
        setResults(data.data?.searchPaginated?.icons || []);
      } catch (err) {
        console.error("Icon search failed", err);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  return (
    <div className={faqStyle.modalOverlay} style={{ zIndex: 10000 }}>
      <div className={faqStyle.modal} style={{ width: "600px" }}>
        <h3 className={faqStyle.modalTitle}>Select an Icon</h3>
        <div className={faqStyle.modalContent}>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Type to search (e.g., 'coffee', 'user')..."
            className={faqStyle.textInput}
            autoFocus
          />
          <div style={{ maxHeight: "400px", overflowY: "auto", marginTop: "15px" }}>
            {loading && <p>Searching...</p>}
            {!loading && results.length === 0 && query && <p>No icons found.</p>}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))", gap: "10px" }}>
              {results.map((icon) => (
                <button
                  key={icon.id}
                  onClick={() => {
                    onSelect(`fa-solid fa-${icon.id}`);
                    onClose();
                  }}
                  style={{
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    background: "#fff",
                  }}
                >
                  <i className={`fa-solid fa-${icon.id}`} style={{ fontSize: "24px", marginBottom: "8px", color: "#333" }}></i>
                  <span style={{ fontSize: "11px", textAlign: "center", wordBreak: "break-word" }}>{icon.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className={faqStyle.modalActions}>
          <button className={faqStyle.modalCancel} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

const Modal = ({ isOpen, onClose, content, hideCloseButton = false }) => {
  if (!isOpen) return null;

  return (
    <div className={faqStyle.modalOverlay}>
      <div className={faqStyle.modal}>
        {!hideCloseButton && (
          <button className={faqStyle.closeButton} onClick={onClose}>
            <IoClose size={20} />
          </button>
        )}
        <div className={faqStyle.modalContent}>{content}</div>
      </div>
    </div>
  );
};

function generateUUID() {
  return (
    "form_" +
    Math.random().toString(36).substr(2, 9) +
    "_" +
    Date.now().toString(36)
  );
}

function parseCustomJSON(customJson, isRoot = false) {
  const rootKey = Object.keys(customJson)[0];
  if (rootKey !== "Q-MC" && rootKey !== "Q-UI") {
    throw new Error('Root must be either "Q-MC" or "Q-UI"');
  }

  const rootObj = customJson[rootKey];

  if (isRoot && rootObj.statement && rootObj.statement.trim() !== "") {
    throw new Error("The root node cannot have a statement.");
  }

  const type = rootKey === "Q-MC" ? "Multiple Choice" : "form_type";

  const question = new Question(
    rootObj.text,
    rootObj.statement || "",
    type,
    rootObj.inputType || "string",
  );

  question.icon = rootObj.icon || "";

  if (rootObj.subQuestions && rootObj.subQuestions.length > 0) {
    rootObj.subQuestions.forEach((subQ) => {
      question.addSubQuestion(parseCustomJSON(subQ, false));
    });
  } else {
    const leafOptions = rootObj.options || [];
    if (leafOptions.length > 1) {
      throw new Error(
        `Leaf node "${question.text}" cannot have more than 1 option. Found ${leafOptions.length}.`
      );
    }
    leafOptions.forEach((o) => {
      question.addNavOption(new Option(o.O, o.description || "", o.icon || ""));
    });
  }

  if (Array.isArray(rootObj.triggers)) {
    question.triggers = normaliseTriggerList(rootObj.triggers);
  }

  return question;
}

class Question {
  constructor(
    text,
    statement = "",
    type = "Multiple Choice",
    inputType = "string",
  ) {
    this.text = text;
    this.statement = statement;
    this.type = type;
    this.subQuestions = [];
    this.options = [];
    this.inputType = inputType;
    this.field = null;
    this.icon = "";

    if (type === "form_type") {
      this.uuid = generateUUID();
      this.triggers = [];
    }

    this.placeholder = "";
    this.required = false;
    this.fieldType = "text";
    this.min = "";
    this.max = "";
    this.step = "";
    this.defaultValue = "";

    this.format = "";

    this.component_id = null;
    this.pinned = false;
    this.pinned_snapshot = null;

    if (type === "field_type") {
      this.options = [];
    }
  }

  setInputType(type) { this.inputType = type; }
  setPlaceholder(placeholder) { this.placeholder = placeholder; }
  setRequired(required) { this.required = required; }
  setFieldType(fieldType) { this.fieldType = fieldType; }
  setOptions(options) { this.options = options; }
  setMin(min) { this.min = min; }
  setMax(max) { this.max = max; }
  setStep(step) { this.step = step; }
  setDefaultValue(defaultValue) { this.defaultValue = defaultValue; }
  setText(text) { this.text = text; }
  setStatement(statement) { this.statement = statement; }
  setFormat(format) { this.format = format; }
  setIcon(icon) { this.icon = icon; }

  addOption(option) { this.options.push(option); }
  removeOption(index) { this.options.splice(index, 1); }
  updateOption(index, option) { this.options[index] = option; }

  addSubQuestion(subQuestion) {
    if (this.type === "form_type" && subQuestion.type !== "field_type") {
      throw new Error(
        "Form-type questions can only have field-type sub-questions.",
      );
    }
    this.subQuestions.push(subQuestion);
  }

  addNavOption(option) {
    if (this.type !== "Multiple Choice") {
      throw new Error("Only Multiple Choice-type questions can have options.");
    }
    this.options.push(option);
  }

  removeNavOption(index) { this.options.splice(index, 1); }
  removeAllNavOptions() { this.options = []; }
  removeSubQuestion(index) { this.subQuestions.splice(index, 1); }

  updateNavOption(index, updatedOption) {
    if (index >= 0 && index < this.options.length) {
      this.options[index] = updatedOption;
    }
  }

  updateSubQuestion(index, updatedQuestion) {
    if (index >= 0 && index < this.subQuestions.length) {
      this.subQuestions[index] = updatedQuestion;
    }
  }

  moveSubQuestionUp(index) {
    if (index > 0) {
      const temp = this.subQuestions[index];
      this.subQuestions[index] = this.subQuestions[index - 1];
      this.subQuestions[index - 1] = temp;
      return true;
    }
    return false;
  }

  moveSubQuestionDown(index) {
    if (index < this.subQuestions.length - 1) {
      const temp = this.subQuestions[index];
      this.subQuestions[index] = this.subQuestions[index + 1];
      this.subQuestions[index + 1] = temp;
      return true;
    }
    return false;
  }

  clone() {
    const cloned = new Question(
      this.text + " (Copy)",
      this.statement,
      this.type,
      this.inputType,
    );

    cloned.icon = this.icon;

    this.options.forEach((option) => {
      cloned.addNavOption(new Option(option.text, option.description, option.icon));
    });

    this.subQuestions.forEach((subQ) => {
      cloned.addSubQuestion(subQ.clone());
    });

    cloned.placeholder = this.placeholder;
    cloned.required = this.required;
    cloned.fieldType = this.fieldType;
    cloned.min = this.min;
    cloned.max = this.max;
    cloned.step = this.step;
    cloned.defaultValue = this.defaultValue;
    cloned.field = this.field;
    cloned.format = this.format;
    cloned.component_id = this.component_id;
    cloned.pinned = this.pinned;
    cloned.pinned_snapshot = this.pinned_snapshot
      ? JSON.parse(JSON.stringify(this.pinned_snapshot))
      : null;

    if (this.type === "form_type") {
      cloned.triggers = normaliseTriggerList(this.triggers || []);
    }

    return cloned;
  }

  toJSON() {
    if (this.type === "form_type") {
      return {
        uuid: this.uuid,
        text: this.text,
        type: this.type,
        statement: this.statement,
        icon: this.icon,
        fields: this.subQuestions.map((q) => {
          const fieldObj = {
            text: q.text,
            type: q.type,
            fieldType: q.fieldType,
            placeholder: q.placeholder,
            required: q.required,
            options: q.options || [],
            min: q.min,
            max: q.max,
            step: q.step,
            defaultValue: q.defaultValue,
            field: q.field || q.text.toLowerCase().replace(/\s+/g, "_"),
            format: q.format || "",
            icon: q.icon || "",
          };
          if (q.component_id) {
            fieldObj.component_id = q.component_id;
            if (q.pinned && q.pinned_snapshot) {
              fieldObj.pinned = true;
              fieldObj.pinned_snapshot = q.pinned_snapshot;
            }
          }
          return fieldObj;
        }),
        triggers: normaliseTriggerList(this.triggers || []),
      };
    }

    return {
      uuid: this.uuid,
      text: this.text,
      type: this.type,
      statement: this.statement,
      icon: this.icon,
      subQuestions: this.subQuestions.map((q) => q.toJSON()),
      options: this.options.map((o) => ({
        text: o.text,
        description: o.description,
        icon: o.icon,
      })),
      inputType: this.inputType,
    };
  }

  toCustomJSON() {
    if (this.type === "form_type") {
      return {
        "Q-UI": {
          uuid: this.uuid,
          text: this.text,
          statement: this.statement,
          icon: this.icon,
          fields: this.subQuestions.map((q) => ({
            text: q.text,
            fieldType: q.fieldType,
            placeholder: q.placeholder,
            required: q.required,
            options: q.options || [],
            type: q.type,
            min: q.min,
            max: q.max,
            step: q.step,
            defaultValue: q.defaultValue,
            format: q.format || "",
            icon: q.icon || "",
          })),
          triggers: normaliseTriggerList(this.triggers || []),
        },
      };
    }

    return {
      [`Q-${this.type === "Multiple Choice" ? "MC" : "UI"}`]: {
        uuid: this.uuid,
        text: this.text,
        statement: this.statement,
        icon: this.icon,
        options: this.options.map((o) => ({
          O: o.text,
          description: o.description,
          icon: o.icon,
        })),
        subQuestions: this.subQuestions.map((q) => q.toCustomJSON()),
        inputType: this.inputType,
      },
    };
  }

  static fromJSON(json, isRoot = false) {
    if (isRoot && json.statement && json.statement.trim() !== "") {
      throw new Error("The root node cannot have a statement.");
    }

    const question = new Question(
      json.text,
      json.statement,
      json.type || "Multiple Choice",
      json.inputType || "string",
    );

    question.uuid = json.uuid || question.uuid;
    question.icon = json.icon || "";

    if (json.type === "form_type" && json.fields) {
      question.subQuestions = json.fields.map((field) => {
        const fieldQuestion = new Question(
          field.text,
          "",
          "field_type",
          field.fieldType || "text",
        );
        fieldQuestion.uuid = field.uuid;
        fieldQuestion.placeholder = field.placeholder || "";
        fieldQuestion.required = field.required || false;
        fieldQuestion.fieldType = field.fieldType || "text";
        fieldQuestion.options = field.options || [];
        fieldQuestion.min = field.min || "";
        fieldQuestion.max = field.max || "";
        fieldQuestion.step = field.step || "";
        fieldQuestion.defaultValue = field.defaultValue || "";
        fieldQuestion.field =
          field.field || field.text.toLowerCase().replace(/\s+/g, "_");
        fieldQuestion.format = field.format || "";
        fieldQuestion.icon = field.icon || "";
        fieldQuestion.component_id = field.component_id || null;
        fieldQuestion.pinned = !!field.pinned;
        fieldQuestion.pinned_snapshot = field.pinned_snapshot || null;
        return fieldQuestion;
      });
      question.triggers = normaliseTriggerList(json.triggers || []);
    } else {
      question.options = (json.options || []).map(
        (o) => new Option(o.text, o.description || "", o.icon || ""),
      );

      if (question.subQuestions.length === 0 && question.options.length > 1) {
        throw new Error(
          `Leaf node "${question.text}" cannot have more than 1 option. Found ${question.options.length}.`
        );
      }

      question.subQuestions = (json.subQuestions || []).map((q) => Question.fromJSON(q, false));
    }

    return question;
  }
}

class Option {
  constructor(text, description = "", icon = "") {
    this.text = text;
    this.description = description;
    this.icon = icon;
  }
}

function StatsBadge({ node }) {
  const subCount = node.subQuestions?.length || 0;
  const optionCount = node.options?.length || 0;
  const fieldCount = node.fields?.length || 0;
  const total = subCount + optionCount + fieldCount;

  if (total === 0) return null;

  return (
    <span
      className={faqStyle.statsBadge}
      title={`${total} item${total > 1 ? "s" : ""} under this`}
    >
      📋 {total}
    </span>
  );
}

function EditQuestionModal({ isOpen, onClose, onSave, initialText }) {
  const [text, setText] = useState(initialText);

  if (!isOpen) return null;

  const handleSave = () => {
    if (text.trim()) {
      onSave(text.trim());
      onClose();
    }
  };

  return (
    <div className={faqStyle.modalOverlay}>
      <div className={faqStyle.modal}>
        <h3 className={faqStyle.modalTitle}>Edit Question</h3>
        <div className={faqStyle.modalContent}>
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Enter question text"
            className={faqStyle.textInput}
            autoFocus
          />
        </div>
        <div className={faqStyle.modalActions}>
          <button className={faqStyle.modalCancel} onClick={onClose}>
            Cancel
          </button>
          <button className={faqStyle.modalConfirm} onClick={handleSave}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function EditStatementModal({ isOpen, onClose, onSave, initialStatement }) {
  const [statement, setStatement] = useState(initialStatement || "");

  if (!isOpen) return null;

  const handleSave = () => {
    onSave(statement);
    onClose();
  };

  return (
    <div className={faqStyle.modalOverlay}>
      <div className={faqStyle.modal}>
        <h3 className={faqStyle.modalTitle}>Edit Statement</h3>
        <div className={faqStyle.modalContent}>
          <textarea
            value={statement}
            onChange={(e) => setStatement(e.target.value)}
            placeholder="Enter statement text"
            rows={4}
            className={faqStyle.textInput}
            autoFocus
          />
        </div>
        <div className={faqStyle.modalActions}>
          <button className={faqStyle.modalCancel} onClick={onClose}>
            Cancel
          </button>
          <button className={faqStyle.modalConfirm} onClick={handleSave}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function PreviewModal({ isOpen, onClose, node }) {
  const [previewTree, setPreviewTree] = useState(null);

  useEffect(() => {
    if (node && isOpen) {
      const clone = node.clone();
      setPreviewTree(clone);
    }
  }, [node, isOpen]);

  if (!isOpen || !previewTree) return null;

  const renderPreview = (previewNode, level = 0, idx = 0) => {
    return (
      <div
        key={previewNode.uuid || `${previewNode.text}-${level}-${idx}`}
        style={{
          marginLeft: level * 20,
          marginBottom: 10,
          padding: 10,
          borderLeft: "2px solid #ddd",
        }}
      >
        <div style={{ fontWeight: "bold" }}>
          {previewNode.icon && <i className={previewNode.icon} style={{ marginRight: 8 }}></i>}
          📝 {previewNode.text}
        </div>
        {previewNode.statement && (
          <div style={{ color: "#666", fontSize: "0.9em", marginTop: 5 }}>
            💬 {previewNode.statement}
          </div>
        )}
        {previewNode.options?.length > 0 && (
          <div style={{ marginTop: 5 }}>
            <strong>Options:</strong>
            <ul style={{ marginTop: 5, marginLeft: 20 }}>
              {previewNode.options.map((opt, optIdx) => (
                <li key={optIdx}>
                  {opt.icon && <i className={opt.icon} style={{ marginRight: 6 }}></i>}
                  {opt.text}
                </li>
              ))}
            </ul>
          </div>
        )}
        {previewNode.subQuestions?.map((child, childIdx) =>
          renderPreview(child, level + 1, childIdx),
        )}
      </div>
    );
  };

  return (
    <div className={faqStyle.modalOverlay}>
      <div className={`${faqStyle.modal} ${faqStyle.previewModal}`}>
        <h3 className={faqStyle.modalTitle}>Preview: {previewTree.text}</h3>
        <div className={faqStyle.previewContent}>
          {renderPreview(previewTree)}
        </div>
        <div className={faqStyle.modalActions}>
          <button className={faqStyle.modalConfirm} onClick={onClose}>
            Close Preview
          </button>
        </div>
      </div>
    </div>
  );
}

function AddQuestionButton({
  path,
  addNode,
  faqStyle,
  parentType,
  onImportFromLibrary,
}) {
  const [showTypeSelector, setShowTypeSelector] = useState(false);
  const [selectedType, setSelectedType] = useState("Multiple Choice");

  const handleAddClick = () => {
    if (parentType === "form_type") {
      const newQuestionText = prompt("Enter a new field:");
      if (!newQuestionText) return;

      const newField = new Question(newQuestionText, "", "field_type");
      const textLower = newQuestionText.toLowerCase();

      if (textLower.includes("email")) {
        newField.fieldType = "email";
        newField.placeholder = "Enter your email address";
      } else if (textLower.includes("phone") || textLower.includes("mobile")) {
        newField.fieldType = "tel";
        newField.placeholder = "Enter your phone number";
      } else if (textLower.includes("date") && textLower.includes("time")) {
        newField.fieldType = "datetime-local";
        newField.placeholder = "Select date and time";
      } else if (textLower.includes("date")) {
        newField.fieldType = "date";
        newField.placeholder = "Select date";
      } else if (textLower.includes("time")) {
        newField.fieldType = "time";
        newField.placeholder = "Select time";
      } else if (textLower.includes("range") || textLower.includes("scale")) {
        newField.fieldType = "range";
        newField.placeholder = "Select value";
        newField.min = "0";
        newField.max = "100";
        newField.step = "1";
      } else if (
        textLower.includes("toggle") ||
        textLower.includes("switch") ||
        textLower.includes("yes/no")
      ) {
        newField.fieldType = "toggle";
        newField.defaultValue = "false";
      } else {
        newField.fieldType = "text";
        newField.placeholder = `Enter your ${newQuestionText.toLowerCase()}`;
      }
      newField.field = newQuestionText.toLowerCase().replace(/\s+/g, "_");

      addNode(path, newField);
    } else {
      setShowTypeSelector(true);
    }
  };

  const handleTypeConfirm = () => {
    setShowTypeSelector(false);

    const promptMessage = selectedType === "form_type"
      ? "Enter the form name:"
      : "Enter a new question:";

    const newText = prompt(promptMessage);
    if (!newText) return;

    const newQuestion = new Question(newText, "", selectedType);
    addNode(path, newQuestion);
  };

  const isFormParent = parentType === "form_type";

  return (
    <>
      {isFormParent ? (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button
            className={`${faqStyle.actionBtn} ${faqStyle.addQst}`}
            onClick={handleAddClick}
            style={{ flex: "1 1 auto", minWidth: "150px" }}
          >
            <MdOutlineAddToPhotos style={{ marginRight: "6px" }} size={22} />
            Add Field
          </button>

          <button
            className={`${faqStyle.actionBtn} ${faqStyle.addQst}`}
            onClick={() => onImportFromLibrary && onImportFromLibrary(path)}
            style={{
              flex: "1 1 auto",
              minWidth: "200px",
              background: "linear-gradient(135deg, #eef3ff 0%, #e0e9ff 100%)",
              border: "1px solid #c7d7fb",
              color: "#3a6fd4",
            }}
            title="Add a field that is backed by a reusable UI Field Component"
          >
            <MdOutlineWidgets style={{ marginRight: "6px" }} size={22} />
            Import from UI Field Components
          </button>
        </div>
      ) : (
        <button
          className={`${faqStyle.actionBtn} ${faqStyle.addQst}`}
          onClick={handleAddClick}
        >
          <MdOutlineAddToPhotos style={{ marginRight: "6px" }} size={22} />
          Add Question / Form
        </button>
      )}

      {showTypeSelector && (
        <div className={faqStyle.modalOverlay}>
          <div className={faqStyle.modal}>
            <h3 className={faqStyle.modalTitle}>Select Question Type</h3>
            <div className={faqStyle.typeOptions}>
              <div
                className={`${faqStyle.typeOption} ${selectedType === "Multiple Choice"
                  ? faqStyle.typeOptionSelected
                  : ""
                  }`}
                onClick={() => setSelectedType("Multiple Choice")}
              >
                <div className={faqStyle.typeRadio}>
                  {selectedType === "Multiple Choice" && (
                    <div className={faqStyle.typeRadioSelected} />
                  )}
                </div>
                <div className={faqStyle.typeContent}>
                  <strong>Multiple Choice</strong>
                  <p>User selects from predefined options</p>
                </div>
              </div>
              <div
                className={`${faqStyle.typeOption} ${selectedType === "form_type"
                  ? faqStyle.typeOptionSelected
                  : ""
                  }`}
                onClick={() => setSelectedType("form_type")}
              >
                <div className={faqStyle.typeRadio}>
                  {selectedType === "form_type" && (
                    <div className={faqStyle.typeRadioSelected} />
                  )}
                </div>
                <div className={faqStyle.typeContent}>
                  <strong>Form</strong>
                  <p>Collect user information through fields</p>
                </div>
              </div>
            </div>
            <div className={faqStyle.modalActions}>
              <button
                className={faqStyle.modalCancel}
                onClick={() => setShowTypeSelector(false)}
              >
                Cancel
              </button>
              <button
                className={faqStyle.modalConfirm}
                onClick={handleTypeConfirm}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const MyQNATest = ({ setBotContent, botContent = null }) => {
  const [showRawLoadModal, setShowRawLoadModal] = useState(false);
  const [rawJSONInput, setRawJSONInput] = useState("");
  const [tree, setTree] = useState(null);
  const [showJSON, setShowJSON] = useState(false);
  const [useCustomJSON, setUseCustomJSON] = useState(true);
  const [collapsed, setCollapsed] = useState({});
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [customJSONInput, setCustomJSONInput] = useState("");
  const skipNextSave = useRef(false);

  const [showEditQuestionModal, setShowEditQuestionModal] = useState(false);
  const [currentEditPath, setCurrentEditPath] = useState([]);
  const [editInitialText, setEditInitialText] = useState("");

  const [showEditStatementModal, setShowEditStatementModal] = useState(false);
  const [currentStatementPath, setCurrentStatementPath] = useState([]);
  const [editInitialStatement, setEditInitialStatement] = useState("");

  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewNode, setPreviewNode] = useState(null);

  const [showRichEditor, setShowRichEditor] = useState(false);
  const [currentPathForOption, setCurrentPathForOption] = useState([]);
  const [optionText, setOptionText] = useState("");
  const [optionDescription, setOptionDescription] = useState("");
  const [editingOptionIndex, setEditingOptionIndex] = useState(-1);
  const [optionIcon, setOptionIcon] = useState("");

  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [currentPathForOptions, setCurrentPathForOptions] = useState([]);
  const [optionsInput, setOptionsInput] = useState("");

  const [library, setLibrary] = useState([]);
  const [libraryVersion, setLibraryVersion] = useState(0);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [pickerTargetPath, setPickerTargetPath] = useState([]);
  const [pickerMode, setPickerMode] = useState("attach-to-existing");

  const [pinModalTarget, setPinModalTarget] = useState(null);
  const [pinModalMode, setPinModalMode] = useState("pin");

  const [validationRules, setValidationRules] = useState([]);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rulesLoadedForFormUuid, setRulesLoadedForFormUuid] = useState(null);
  const rulesSaveTimerRef = useRef(null);

  const [connectorDefs, setConnectorDefs] = useState([]);
  const [operations, setOperations] = useState([]);
  const [connectedProviders, setConnectedProviders] = useState([]);
  const [attachFlowOpen, setAttachFlowOpen] = useState(false);
  const [attachFlowTargetPath, setAttachFlowTargetPath] = useState([]);

  const [showIconPicker, setShowIconPicker] = useState(false);
  const [iconPickerTarget, setIconPickerTarget] = useState(null);

  const [openActionsDropdowns, setOpenActionsDropdowns] = useState({});

  const toggleActionsDropdown = (pathKey) => {
    setOpenActionsDropdowns((prev) => ({
      ...prev,
      [pathKey]: !prev[pathKey],
    }));
  };

  useEffect(() => {
    const fetchLibrary = async () => {
      const appName = botContent?.app_name;
      const region = (botContent?.region || "us").toLowerCase();
      const baseUrl =
        process.env.REACT_APP_SL_BE_BASE_ENV_PART +
        "/" +
        process.env.REACT_APP_STAGE_ENV;
      const token = localStorage.getItem("token");
      if (!appName || !token) return;

      setLibraryLoading(true);
      try {
        const resp = await fetch(
          `https://${region}.autobot2.${baseUrl}/ui_field_component?app_name=${encodeURIComponent(
            appName,
          )}`,
          { headers: { Authorization: token } },
        );
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        setLibrary(Array.isArray(data.components) ? data.components : []);
        setLibraryVersion(Number.isFinite(data.version) ? data.version : 0);
      } catch (err) {
        console.error("[MyQNATest] library fetch failed:", err);
        setLibrary([]);
      } finally {
        setLibraryLoading(false);
      }
    };

    fetchLibrary();
  }, [
    botContent?.app_name,
    botContent?.region,
    botContent?.library_version,
  ]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!botContent?.app_name) return;
      try {
        const data = await listDefinitions({
          appName: botContent.app_name,
          region: botContent?.region,
        });
        if (!cancelled) setConnectorDefs(data.definitions || []);
      } catch (err) {
        if (!cancelled) {
          console.warn("[MyQNATest] definitions fetch failed:", err);
          setConnectorDefs([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [botContent?.app_name, botContent?.region]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!botContent?.region) return;
      try {
        const ops = await listOperations({ region: botContent.region });
        if (!cancelled) setOperations(ops.operations || []);
      } catch (err) {
        if (!cancelled) {
          console.warn("[MyQNATest] operations fetch failed:", err);
          setOperations([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [botContent?.region]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!botContent?.app_name) return;
      try {
        const data = await listConnections({
          appName: botContent.app_name,
          region: botContent?.region,
        });
        if (!cancelled) {
          const providers = Array.isArray(data.connections)
            ? [...new Set(data.connections.map((c) => c.provider))]
            : [];
          setConnectedProviders(providers);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn("[MyQNATest] connections fetch failed:", err);
          setConnectedProviders([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [botContent?.app_name, botContent?.region]);

  useEffect(() => {
    const formUuid = tree?.uuid;
    const appName = botContent?.app_name;

    if (!formUuid || !appName) {
      setValidationRules([]);
      setRulesLoadedForFormUuid(null);
      return;
    }

    if (rulesLoadedForFormUuid === formUuid) return;

    let cancelled = false;
    setRulesLoading(true);

    (async () => {
      const rules = await fetchRulesForForm({
        appName,
        formUuid,
        region: botContent?.region,
      });
      if (cancelled) return;
      setValidationRules(rules);
      setRulesLoadedForFormUuid(formUuid);
      setRulesLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [
    tree?.uuid,
    botContent?.app_name,
    botContent?.region,
    rulesLoadedForFormUuid,
  ]);

  useEffect(() => {
    return () => {
      if (rulesSaveTimerRef.current) {
        clearTimeout(rulesSaveTimerRef.current);
        rulesSaveTimerRef.current = null;
      }
    };
  }, [tree?.uuid]);

  const handleRulesChange = useCallback(
    (nextRules) => {
      setValidationRules(nextRules);

      const formUuid = tree?.uuid;
      const appName = botContent?.app_name;
      if (!formUuid || !appName) return;

      if (rulesSaveTimerRef.current) {
        clearTimeout(rulesSaveTimerRef.current);
      }

      rulesSaveTimerRef.current = setTimeout(async () => {
        rulesSaveTimerRef.current = null;
        try {
          await saveRulesForForm({
            appName,
            formUuid,
            rules: nextRules,
            region: botContent?.region,
          });
        } catch (err) {
          console.error("[MyQNATest] saveRulesForForm failed:", err);
          alert(`Could not save validation rules: ${err.message}`);
        }
      }, 1500);
    },
    [tree?.uuid, botContent?.app_name, botContent?.region],
  );

  const handleLoadFromRawJSON = () => {
    try {
      const parsed = JSON.parse(rawJSONInput);
      const rootQuestion = Question.fromJSON(parsed, true);
      if (rootQuestion.type !== "Multiple Choice") {
        throw new Error(
          'Root question must be "Multiple Choice" type. Found: ' +
          rootQuestion.type,
        );
      }
      setTree(rootQuestion);
      setShowRawLoadModal(false);
      setRawJSONInput("");
    } catch (error) {
      alert("Error parsing Raw JSON: " + error.message);
    }
  };

  const handleSaveOption = () => {
    if (optionText.trim()) {
      if (editingOptionIndex > -1) {
        updateOption(currentPathForOption, editingOptionIndex, new Option(optionText, optionDescription, optionIcon));
      } else {
        addNode(currentPathForOption, new Option(optionText, optionDescription, optionIcon));
      }
    }
    setOptionText("");
    setOptionDescription("");
    setOptionIcon("");
    setEditingOptionIndex(-1);
    setShowRichEditor(false);
  };

  const updateFieldType = (path, newFieldType) => {
    const recursiveUpdate = (currentNode, path) => {
      if (!(currentNode instanceof Question)) {
        Object.setPrototypeOf(currentNode, Question.prototype);
      }
      if (path.length === 0) {
        currentNode.setFieldType(newFieldType);
        if (!["select", "radio", "checkbox"].includes(newFieldType)) {
          currentNode.setOptions([]);
        }
        if (newFieldType !== "range") {
          currentNode.setMin("");
          currentNode.setMax("");
          currentNode.setStep("");
        }
        if (newFieldType !== "toggle") {
          currentNode.setDefaultValue("");
        }
        return;
      }
      const [nextIndex, ...restPath] = path;
      recursiveUpdate(currentNode.subQuestions[nextIndex], restPath);
    };
    if (!tree) return;
    const newTree = Question.fromJSON(tree.toJSON());
    recursiveUpdate(newTree, path);
    setTree(newTree);
  };

  const updatePlaceholder = (path, newPlaceholder) => {
    const recursiveUpdate = (currentNode, path) => {
      if (!(currentNode instanceof Question)) {
        Object.setPrototypeOf(currentNode, Question.prototype);
      }
      if (path.length === 0) {
        currentNode.setPlaceholder(newPlaceholder);
        return;
      }
      const [nextIndex, ...restPath] = path;
      recursiveUpdate(currentNode.subQuestions[nextIndex], restPath);
    };
    if (!tree) return;
    const newTree = Question.fromJSON(tree.toJSON());
    recursiveUpdate(newTree, path);
    setTree(newTree);
  };

  const updateRequired = (path, newRequired) => {
    const recursiveUpdate = (currentNode, path) => {
      if (!(currentNode instanceof Question)) {
        Object.setPrototypeOf(currentNode, Question.prototype);
      }
      if (path.length === 0) {
        currentNode.setRequired(newRequired);
        return;
      }
      const [nextIndex, ...restPath] = path;
      recursiveUpdate(currentNode.subQuestions[nextIndex], restPath);
    };
    if (!tree) return;
    const newTree = Question.fromJSON(tree.toJSON());
    recursiveUpdate(newTree, path);
    setTree(newTree);
  };

  const updateOptions = (path, newOptions) => {
    const recursiveUpdate = (currentNode, path) => {
      if (!(currentNode instanceof Question)) {
        Object.setPrototypeOf(currentNode, Question.prototype);
      }
      if (path.length === 0) {
        currentNode.setOptions(newOptions);
        return;
      }
      const [nextIndex, ...restPath] = path;
      recursiveUpdate(currentNode.subQuestions[nextIndex], restPath);
    };
    if (!tree) return;
    const newTree = Question.fromJSON(tree.toJSON());
    recursiveUpdate(newTree, path);
    setTree(newTree);
  };

  const updateMin = (path, newMin) => {
    const recursiveUpdate = (currentNode, path) => {
      if (!(currentNode instanceof Question)) {
        Object.setPrototypeOf(currentNode, Question.prototype);
      }
      if (path.length === 0) {
        currentNode.setMin(newMin);
        return;
      }
      const [nextIndex, ...restPath] = path;
      recursiveUpdate(currentNode.subQuestions[nextIndex], restPath);
    };
    if (!tree) return;
    const newTree = Question.fromJSON(tree.toJSON());
    recursiveUpdate(newTree, path);
    setTree(newTree);
  };

  const updateMax = (path, newMax) => {
    const recursiveUpdate = (currentNode, path) => {
      if (!(currentNode instanceof Question)) {
        Object.setPrototypeOf(currentNode, Question.prototype);
      }
      if (path.length === 0) {
        currentNode.setMax(newMax);
        return;
      }
      const [nextIndex, ...restPath] = path;
      recursiveUpdate(currentNode.subQuestions[nextIndex], restPath);
    };
    if (!tree) return;
    const newTree = Question.fromJSON(tree.toJSON());
    recursiveUpdate(newTree, path);
    setTree(newTree);
  };

  const updateStep = (path, newStep) => {
    const recursiveUpdate = (currentNode, path) => {
      if (!(currentNode instanceof Question)) {
        Object.setPrototypeOf(currentNode, Question.prototype);
      }
      if (path.length === 0) {
        currentNode.setStep(newStep);
        return;
      }
      const [nextIndex, ...restPath] = path;
      recursiveUpdate(currentNode.subQuestions[nextIndex], restPath);
    };
    if (!tree) return;
    const newTree = Question.fromJSON(tree.toJSON());
    recursiveUpdate(newTree, path);
    setTree(newTree);
  };

  const updateDefaultValue = (path, newDefaultValue) => {
    const recursiveUpdate = (currentNode, path) => {
      if (!(currentNode instanceof Question)) {
        Object.setPrototypeOf(currentNode, Question.prototype);
      }
      if (path.length === 0) {
        currentNode.setDefaultValue(newDefaultValue);
        return;
      }
      const [nextIndex, ...restPath] = path;
      recursiveUpdate(currentNode.subQuestions[nextIndex], restPath);
    };
    if (!tree) return;
    const newTree = Question.fromJSON(tree.toJSON());
    recursiveUpdate(newTree, path);
    setTree(newTree);
  };

  const handleOpenOptionsModal = (path, currentNode) => {
    setCurrentPathForOptions(path);
    const optionsText = currentNode.options
      .map((opt) => (typeof opt === "object" ? opt.label || opt.value : opt))
      .join(", ");
    setOptionsInput(optionsText);
    setShowOptionsModal(true);
  };

  const handleSaveOptions = () => {
    if (!optionsInput.trim()) {
      updateOptions(currentPathForOptions, []);
      setShowOptionsModal(false);
      return;
    }
    const newOptions = optionsInput
      .split(",")
      .map((opt) => opt.trim())
      .filter((opt) => opt.length > 0)
      .map((opt) => ({
        value: opt.toLowerCase().replace(/\s+/g, "_"),
        label: opt,
      }));
    updateOptions(currentPathForOptions, newOptions);
    setShowOptionsModal(false);
  };

  const handlePickerSelect = (component) => {
    if (pickerMode === "create-new") {
      const newField = new Question(
        component.name || "Untitled Field",
        "",
        "field_type",
      );

      const baseKey = (component.name || "field")
        .toLowerCase()
        .trim()
        .replace(/\s+/g, "_")
        .replace(/[^a-z0-9_]/g, "");
      newField.field = baseKey || "field";

      newField.component_id = component.component_id;
      newField.fieldType = component.type || "text";
      newField.options = Array.isArray(component.options)
        ? component.options.map((o) => ({ value: o.value, label: o.label }))
        : [];
      newField.min = component.min ?? "";
      newField.max = component.max ?? "";
      newField.step = component.step ?? "";
      newField.placeholder = component.default_placeholder || "";
      newField.pinned = false;
      newField.pinned_snapshot = null;

      addNode(pickerTargetPath, newField);

      setShowPicker(false);
      setPickerMode("attach-to-existing");
      return;
    }

    const applyComponent = (node, path) => {
      if (!(node instanceof Question)) {
        Object.setPrototypeOf(node, Question.prototype);
      }
      if (path.length === 0) {
        node.component_id = component.component_id;
        node.fieldType = component.type || "text";
        node.options = Array.isArray(component.options)
          ? component.options.map((o) => ({ value: o.value, label: o.label }))
          : [];
        node.min = component.min ?? "";
        node.max = component.max ?? "";
        node.step = component.step ?? "";
        if (!node.placeholder && component.default_placeholder) {
          node.placeholder = component.default_placeholder;
        }
        node.pinned = false;
        node.pinned_snapshot = null;
        return;
      }
      const [nextIndex, ...restPath] = path;
      applyComponent(node.subQuestions[nextIndex], restPath);
    };

    if (!tree) return;
    const newTree = Question.fromJSON(tree.toJSON());
    applyComponent(newTree, pickerTargetPath);
    setTree(newTree);
    setShowPicker(false);
    setPickerMode("attach-to-existing");
  };

  const applyPinDecision = (confirmed) => {
    if (!confirmed || !pinModalTarget) {
      setPinModalTarget(null);
      return;
    }

    const { path } = pinModalTarget;
    const shouldPin = pinModalMode === "pin";

    const mutate = (node, p) => {
      if (!(node instanceof Question)) {
        Object.setPrototypeOf(node, Question.prototype);
      }
      if (p.length === 0) {
        if (shouldPin) {
          const resolved = resolveFieldComponent(node, library);
          node.pinned = true;
          node.pinned_snapshot = {
            fieldType: resolved.fieldType,
            options: Array.isArray(resolved.options)
              ? resolved.options.map((o) =>
                typeof o === "object"
                  ? { value: o.value, label: o.label }
                  : { value: String(o), label: String(o) },
              )
              : [],
            min: resolved.min ?? "",
            max: resolved.max ?? "",
            step: resolved.step ?? "",
          };
        } else {
          node.pinned = false;
          node.pinned_snapshot = null;
        }
        return;
      }
      const [nextIndex, ...restPath] = p;
      mutate(node.subQuestions[nextIndex], restPath);
    };

    if (!tree) {
      setPinModalTarget(null);
      return;
    }

    const newTree = Question.fromJSON(tree.toJSON());
    mutate(newTree, path);
    setTree(newTree);
    setPinModalTarget(null);
  };

  const requestPinToggle = (path, node, attachedComponent) => {
    setPinModalTarget({
      path,
      fieldLabel: node.text || "Untitled Field",
      componentName: attachedComponent?.name || null,
    });
    setPinModalMode(node.pinned ? "unpin" : "pin");
  };

  const handleLoadFromJSON = () => {
    try {
      const parsed = JSON.parse(customJSONInput);
      const rootKey = Object.keys(parsed)[0];
      if (rootKey !== "Q-MC") {
        throw new Error(
          'Custom JSON root must be "Q-MC" for Multiple Choice. Found: ' +
          rootKey,
        );
      }
      const rootQuestion = parseCustomJSON(parsed, true);
      if (rootQuestion.type !== "Multiple Choice") {
        throw new Error(
          'Root question must be "Multiple Choice" type. Found: ' +
          rootQuestion.type,
        );
      }
      setTree(rootQuestion);
      setShowLoadModal(false);
      setCustomJSONInput("");
    } catch (error) {
      alert("Error parsing JSON: " + error.message);
    }
  };

  useEffect(() => {
    if (botContent?.FAQ) {
      try {
        const faq = botContent.FAQ;
        const hasContent =
          faq.text?.trim() ||
          faq.statement?.trim() ||
          (faq.options && faq.options.length > 0) ||
          (faq.subQuestions && faq.subQuestions.length > 0);

        if (hasContent) {
          skipNextSave.current = true;
          const faqTree = Question.fromJSON(faq, true);
          setTree(faqTree);
        }
      } catch (error) {
        console.error("Error initializing FAQ tree:", error);
        alert("Invalid FAQ structure in botContent");
      }
    }
  }, [botContent]);

  useEffect(() => {
    if (skipNextSave.current) {
      skipNextSave.current = false;
      return;
    }
    if (tree) {
      setBotContent((prev) => ({ ...prev, FAQ: tree.toJSON() }));
    } else {
      setBotContent((prev) => ({ ...prev, FAQ: null }));
    }
  }, [tree, setBotContent]);

  const toggleCollapse = (pathKey) => {
    setCollapsed((prev) => ({ ...prev, [pathKey]: !prev[pathKey] }));
  };

  const addNode = (path, newNode) => {
    const recursiveAdd = (currentNode, path) => {
      if (!(currentNode instanceof Question)) {
        Object.setPrototypeOf(currentNode, Question.prototype);
      }
      if (currentNode.type === "form_type" && path.length > 0) {
        alert("Forms can only have fields one level deep");
        return;
      }
      if (path.length === 0) {
        if (newNode instanceof Option) {
          currentNode.addNavOption(newNode);
        } else {
          currentNode.addSubQuestion(newNode);
        }
        return;
      }
      const [nextIndex, ...restPath] = path;
      recursiveAdd(currentNode.subQuestions[nextIndex], restPath);
    };
    const newTree = tree
      ? Question.fromJSON(tree.toJSON())
      : new Question("");
    recursiveAdd(newTree, path);
    setTree(newTree);
  };

  const addStatement = (path) => {
    const recursiveUpdate = (currentNode, path) => {
      if (!(currentNode instanceof Question)) {
        Object.setPrototypeOf(currentNode, Question.prototype);
      }
      if (path.length === 0) {
        const newStatement = prompt("Enter a statement:");
        if (newStatement !== null) {
          currentNode.setStatement(newStatement);
        }
        return;
      }
      const [nextIndex, ...restPath] = path;
      recursiveUpdate(currentNode.subQuestions[nextIndex], restPath);
    };
    const newTree = Question.fromJSON(tree.toJSON());
    recursiveUpdate(newTree, path);
    setTree(newTree);
  };

  const updateNodeText = (path, newText) => {
    const recursiveUpdate = (currentNode, path) => {
      if (!(currentNode instanceof Question)) {
        Object.setPrototypeOf(currentNode, Question.prototype);
      }
      if (path.length === 0) {
        currentNode.setText(newText);
        return;
      }
      const [nextIndex, ...restPath] = path;
      recursiveUpdate(currentNode.subQuestions[nextIndex], restPath);
    };
    if (!tree) return;
    const newTree = Question.fromJSON(tree.toJSON());
    recursiveUpdate(newTree, path);
    setTree(newTree);
  };

  const updateOption = (path, optionIndex, updatedOption) => {
    const recursiveUpdate = (currentNode, path) => {
      if (!(currentNode instanceof Question)) {
        Object.setPrototypeOf(currentNode, Question.prototype);
      }
      if (path.length === 0) {
        currentNode.updateNavOption(optionIndex, updatedOption);
        return;
      }
      const [nextIndex, ...restPath] = path;
      recursiveUpdate(currentNode.subQuestions[nextIndex], restPath);
    };
    if (!tree) return;
    const newTree = Question.fromJSON(tree.toJSON());
    recursiveUpdate(newTree, path);
    setTree(newTree);
  };

  const removeOption = (path, optionIndex) => {
    if (!window.confirm("Are you sure you want to remove this answer?")) return;

    const recursiveRemove = (currentNode, path) => {
      if (!(currentNode instanceof Question)) {
        Object.setPrototypeOf(currentNode, Question.prototype);
      }
      if (path.length === 0) {
        currentNode.removeNavOption(optionIndex);
        return;
      }
      const [nextIndex, ...restPath] = path;
      recursiveRemove(currentNode.subQuestions[nextIndex], restPath);
    };
    if (!tree) return;
    const newTree = Question.fromJSON(tree.toJSON());
    recursiveRemove(newTree, path);
    setTree(newTree);
  };

  const updateStatement = (path, newStatement) => {
    const recursiveUpdate = (currentNode, path) => {
      if (!(currentNode instanceof Question)) {
        Object.setPrototypeOf(currentNode, Question.prototype);
      }
      if (path.length === 0) {
        currentNode.setStatement(newStatement);
        return;
      }
      const [nextIndex, ...restPath] = path;
      recursiveUpdate(currentNode.subQuestions[nextIndex], restPath);
    };
    if (!tree) return;
    const newTree = Question.fromJSON(tree.toJSON());
    recursiveUpdate(newTree, path);
    setTree(newTree);
  };

  const updateIcon = (target, icon) => {
    const { type, path, optionIndex } = target;
    const recursiveUpdate = (currentNode, path) => {
      if (!(currentNode instanceof Question)) {
        Object.setPrototypeOf(currentNode, Question.prototype);
      }
      if (path.length === 0) {
        if (type === "question") {
          currentNode.setIcon(icon);
        } else if (type === "option" && optionIndex !== undefined) {
          const opt = currentNode.options[optionIndex];
          currentNode.options[optionIndex] = new Option(opt.text, opt.description, icon);
        }
        return;
      }
      const [nextIndex, ...restPath] = path;
      recursiveUpdate(currentNode.subQuestions[nextIndex], restPath);
    };
    if (!tree) return;
    const newTree = Question.fromJSON(tree.toJSON());
    recursiveUpdate(newTree, path);
    setTree(newTree);
  };

  const cloneNode = (path) => {
    const getNodeAtPath = (currentNode, path) => {
      if (!(currentNode instanceof Question)) {
        Object.setPrototypeOf(currentNode, Question.prototype);
      }
      if (path.length === 0) {
        return currentNode;
      }
      const [nextIndex, ...restPath] = path;
      if (currentNode.subQuestions && currentNode.subQuestions[nextIndex]) {
        return getNodeAtPath(currentNode.subQuestions[nextIndex], restPath);
      }
      return null;
    };

    const nodeToClone = getNodeAtPath(tree, path);
    if (nodeToClone) {
      const cloned = nodeToClone.clone();
      const parentPath = path.slice(0, -1);
      const insertAtIndex = path[path.length - 1] + 1;

      const insertNode = (currentNode, targetPath, index, node) => {
        if (!(currentNode instanceof Question)) {
          Object.setPrototypeOf(currentNode, Question.prototype);
        }
        if (targetPath.length === 0) {
          currentNode.subQuestions.splice(index, 0, node);
          return true;
        }
        const [nextIndex, ...restPath] = targetPath;
        return insertNode(
          currentNode.subQuestions[nextIndex],
          restPath,
          index,
          node,
        );
      };

      const newTree = Question.fromJSON(tree.toJSON());
      insertNode(newTree, parentPath, insertAtIndex, cloned);
      setTree(newTree);
    }
  };

  const moveNode = (path, direction) => {
    if (path.length === 0) return;

    const parentPath = path.slice(0, -1);
    const currentIndex = path[path.length - 1];
    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0) return;

    const moveInTree = (currentNode, targetPath, fromIdx, toIdx) => {
      if (!(currentNode instanceof Question)) {
        Object.setPrototypeOf(currentNode, Question.prototype);
      }
      if (targetPath.length === 0) {
        if (direction === "up") {
          currentNode.moveSubQuestionUp(fromIdx);
        } else {
          currentNode.moveSubQuestionDown(fromIdx);
        }
        return true;
      }
      const [nextIndex, ...restPath] = targetPath;
      return moveInTree(
        currentNode.subQuestions[nextIndex],
        restPath,
        fromIdx,
        toIdx,
      );
    };

    const newTree = Question.fromJSON(tree.toJSON());
    moveInTree(newTree, parentPath, currentIndex, newIndex);
    setTree(newTree);
  };

  const removeNode = (path) => {
    const countChildren = (node) => {
      let count = 0;
      if (node.subQuestions) {
        count += node.subQuestions.length;
        node.subQuestions.forEach((child) => {
          count += countChildren(child);
        });
      }
      if (node.options) {
        count += node.options.length;
      }
      if (node.fields) {
        count += node.fields.length;
      }
      return count;
    };

    const getNodeAtPath = (currentNode, path) => {
      if (!(currentNode instanceof Question)) {
        Object.setPrototypeOf(currentNode, Question.prototype);
      }
      if (path.length === 0) {
        return currentNode;
      }
      const [nextIndex, ...restPath] = path;
      if (currentNode.subQuestions && currentNode.subQuestions[nextIndex]) {
        return getNodeAtPath(currentNode.subQuestions[nextIndex], restPath);
      }
      return null;
    };

    let nodeToRemove = null;
    if (tree) {
      nodeToRemove = getNodeAtPath(tree, path);
    }

    if (nodeToRemove) {
      const childrenCount = countChildren(nodeToRemove);
      const hasChildren = childrenCount > 0;
      const isRoot = path.length === 0;

      let message = "";
      if (isRoot) {
        message =
          "⚠️ WARNING: You are about to delete the ENTIRE FAQ Tree!\n\nThis will remove all questions, options, and forms you've created.\n\nAre you sure you want to proceed?";
      } else if (hasChildren) {
        const itemType =
          nodeToRemove.type === "form_type" ? "form" : "question";
        const childrenType =
          nodeToRemove.options?.length > 0
            ? "options"
            : nodeToRemove.fields?.length > 0
              ? "fields"
              : "sub-questions";
        message = `⚠️ WARNING: You are about to delete this ${itemType} "${nodeToRemove.text}"\n\nIt contains ${childrenCount} ${childrenType} that will also be deleted.\n\nAre you sure you want to proceed?`;
      } else {
        const itemType =
          nodeToRemove.type === "form_type" ? "form" : "question";
        message = `Are you sure you want to delete this ${itemType} "${nodeToRemove.text}"?`;
      }

      if (window.confirm(message)) {
        const recursiveRemove = (currentNode, path) => {
          if (!(currentNode instanceof Question)) {
            Object.setPrototypeOf(currentNode, Question.prototype);
          }
          if (path.length === 1) {
            currentNode.removeSubQuestion(path[0]);
            return;
          }
          const [nextIndex, ...restPath] = path;
          recursiveRemove(currentNode.subQuestions[nextIndex], restPath);
        };

        if (!tree) return;
        const newTree = Question.fromJSON(tree.toJSON());
        if (path.length === 0) {
          setTree(null);
        } else {
          recursiveRemove(newTree, path);
          setTree(newTree);
        }
      }
    } else {
      const recursiveRemove = (currentNode, path) => {
        if (!(currentNode instanceof Question)) {
          Object.setPrototypeOf(currentNode, Question.prototype);
        }
        if (path.length === 1) {
          currentNode.removeSubQuestion(path[0]);
          return;
        }
        const [nextIndex, ...restPath] = path;
        recursiveRemove(currentNode.subQuestions[nextIndex], restPath);
      };
      if (!tree) return;
      const newTree = Question.fromJSON(tree.toJSON());
      if (path.length === 0) {
        if (
          window.confirm(
            "⚠️ WARNING: You are about to delete the ENTIRE FAQ Tree!\n\nThis will remove all questions, options, and forms you've created.\n\nAre you sure you want to proceed?",
          )
        ) {
          setTree(null);
        }
      } else {
        if (window.confirm("Are you sure you want to delete this item?")) {
          recursiveRemove(newTree, path);
          setTree(newTree);
        }
      }
    }
  };

  const openTriggerAttachFlow = (path) => {
    setAttachFlowTargetPath(path);
    setAttachFlowOpen(true);
  };

  const closeTriggerAttachFlow = () => {
    setAttachFlowOpen(false);
    setAttachFlowTargetPath([]);
  };

  const handleAttachTrigger = (attachment) => {
    if (!tree || !Array.isArray(attachFlowTargetPath)) return;

    const newTree = Question.fromJSON(tree.toJSON());
    const mutate = (node, p) => {
      if (!(node instanceof Question)) {
        Object.setPrototypeOf(node, Question.prototype);
      }
      if (p.length === 0) {
        if (!Array.isArray(node.triggers)) node.triggers = [];
        node.triggers = [...node.triggers, attachment];
        return;
      }
      const [nextIndex, ...restPath] = p;
      if (node.subQuestions?.[nextIndex]) {
        mutate(node.subQuestions[nextIndex], restPath);
      }
    };
    mutate(newTree, attachFlowTargetPath);
    setTree(newTree);
    closeTriggerAttachFlow();
  };

  const getNodeForPath = (root, p) => {
    let cur = root;
    for (const idx of p) {
      if (!cur || !Array.isArray(cur.subQuestions)) return null;
      cur = cur.subQuestions[idx];
    }
    return cur;
  };

  const renderTree = (node, path = [], parentType = null) => {
    const isRoot = path.length === 0;
    const isLeaf = node.subQuestions.length === 0;
    const isField = node.type === "field_type" && parentType === "form_type";
    const isForm = node.type === "form_type";

    const showAddQuestionButton =
      (node.type === "Multiple Choice" ||
        (node.type === "form_type" && parentType !== "form_type")) &&
      node.options.length === 0;
    const showAddOptionButton =
      !isRoot &&
      node.type === "Multiple Choice" &&
      isLeaf &&
      node.options.length === 0;

    const pathKey = path.join("-");
    const isCollapsed = collapsed[pathKey] || false;

    const isMissingChildren =
      !isField &&
      ((node.type === "Multiple Choice" &&
        isLeaf &&
        node.options.length === 0) ||
        (node.type === "form_type" &&
          isLeaf &&
          parentType !== "form_type" &&
          node.subQuestions.length === 0));

    const canMoveUp = path.length > 0 && path[path.length - 1] > 0;
    const canMoveDown =
      path.length > 0 &&
      path[path.length - 1] <
      (parentType
        ? parentType === "form_type"
          ? tree?.subQuestions?.length - 1
          : tree?.subQuestions?.length - 1
        : 0);

    const attachedComponent = node.component_id
      ? library.find((c) => c.component_id === node.component_id)
      : null;
    const resolved = isField ? resolveFieldComponent(node, library) : null;
    const effectiveFieldType = resolved ? resolved.fieldType : node.fieldType;
    const effectiveOptions = resolved ? resolved.options : node.options;

    const isActionsOpen = openActionsDropdowns[pathKey] || false;
    const triggerCount = Array.isArray(node.triggers) ? node.triggers.length : 0;
    const validationCount = isForm ? validationRules.length : 0;

    let typeBadge = null;
    if (isRoot) {
      typeBadge = <span className={`${faqStyle.badge}`} style={{ background: "#e0e7ff", color: "#3730a3" }}>ROOT</span>;
    } else if (isForm) {
      typeBadge = <span className={`${faqStyle.badge}`} style={{ background: "#dcfce7", color: "#166534" }}>FORM</span>;
    } else if (isField) {
      typeBadge = <span className={`${faqStyle.badge}`} style={{ background: "#f3f4f6", color: "#4b5563" }}>FIELD</span>;
    } else {
      typeBadge = <span className={`${faqStyle.badge} ${faqStyle[node.type.replace(/\s+/g, "").toLowerCase()]}`}>MCQ</span>;
    }

    return (
      <div
        className={`${faqStyle.card} ${isMissingChildren ? faqStyle.missingChildren : ""}`}
      >
        <div className={faqStyle.cardHeader}>
          <button
            className={faqStyle.collapseBtn}
            onClick={() => toggleCollapse(pathKey)}
          >
            {isCollapsed ? "▶" : "▼"}
          </button>

          <div style={{ display: 'flex', alignItems: 'center', marginRight: '8px' }}>
            <button
              onClick={() => {
                setIconPickerTarget({ type: 'question', path });
                setShowIconPicker(true);
              }}
              title="Select Icon"
              style={{ background: 'none', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              {node.icon ? <i className={node.icon} style={{ fontSize: '16px', color: '#4a7feb' }}></i> : <FaSearch size={12} color="#666" />}
            </button>
          </div>

          <span className={faqStyle.questionText} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* Show a fallback emoji only if NO custom icon is selected, otherwise just the text */}
            {isField ? (
              <>
                {!node.icon && '🔧 '} {node.text}
              </>
            ) : isForm ? (
              <>{!node.icon && '📋 '} {node.text}</>
            ) : (
              <>{!node.icon && '❓ '} {node.text}</>
            )}
            {typeBadge}
          </span>

          <StatsBadge node={node} />
          <button
            className={faqStyle.editBtn}
            onClick={() => {
              setCurrentEditPath(path);
              setEditInitialText(node.text);
              setShowEditQuestionModal(true);
            }}
            title="Edit question text"
          >
            <FaEdit />
          </button>
          {!isRoot && (
            <>
              <button
                className={faqStyle.moveBtn}
                onClick={() => moveNode(path, "up")}
                disabled={!canMoveUp}
                title="Move up"
              >
                <FaArrowUp />
              </button>
              <button
                className={faqStyle.moveBtn}
                onClick={() => moveNode(path, "down")}
                disabled={!canMoveDown}
                title="Move down"
              >
                <FaArrowDown />
              </button>
            </>
          )}
          <button
            className={faqStyle.cloneBtn}
            onClick={() => cloneNode(path)}
            title="Clone this question"
          >
            <FaCopy />
          </button>
          <button
            className={faqStyle.previewBtn}
            onClick={() => {
              setPreviewNode(node);
              setShowPreviewModal(true);
            }}
            title="Preview this question and its children"
          >
            <FaEye />
          </button>

          {!isRoot && (
            <button
              className={faqStyle.removeBtn}
              onClick={() => removeNode(path)}
              title="Remove"
              style={{ marginLeft: 4, color: "#dc3545", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center" }}
            >
              <RiDeleteBin6Line size={16} />
            </button>
          )}

          {isForm && <span className={faqStyle.uuidBadge}>ID: {node.uuid}</span>}
        </div>

        {!isCollapsed && (
          <>
            {!isRoot && node.statement && (
              <div className={faqStyle.statementWrapper}>
                <p className={faqStyle.statement}>📝 {node.statement}</p>
                <button
                  className={faqStyle.editStatementBtn}
                  onClick={() => {
                    setCurrentStatementPath(path);
                    setEditInitialStatement(node.statement);
                    setShowEditStatementModal(true);
                  }}
                  title="Edit statement"
                >
                  <FaEdit size={12} />
                </button>
              </div>
            )}

            {/* TOP Add Field / Import buttons for forms */}
            {isForm && parentType !== "form_type" && (
              <div style={{ marginTop: 10, marginBottom: 10 }}>
                <AddQuestionButton
                  path={path}
                  addNode={addNode}
                  faqStyle={faqStyle}
                  parentType={node.type}
                  onImportFromLibrary={(fieldPath) => {
                    setPickerTargetPath(fieldPath);
                    setPickerMode("create-new");
                    setShowPicker(true);
                  }}
                />
              </div>
            )}

            {node.type === "Multiple Choice" && node.options.length > 0 && (
              <div className={faqStyle.answersSection}>
                <div className={faqStyle.sectionTitle}>
                  🔀 Answers (each leads to a child question):
                </div>
                {node.options.map((opt, idx) => (
                  <div key={idx} className={faqStyle.answerItem}>
                    <div className={faqStyle.answerContent} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                      <div style={{ marginTop: '2px' }}>
                        {opt.icon && <i className={opt.icon} style={{ fontSize: '16px', color: '#4a7feb' }}></i>}
                      </div>
                      <div>
                        <div>
                          <strong>{idx + 1}.</strong> {opt.text}
                        </div>
                        {opt.description && opt.description !== "<p><br></p>" && (
                          <div
                            className={faqStyle.answerDescription}
                            dangerouslySetInnerHTML={{ __html: opt.description }}
                          />
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
                      <button
                        className={faqStyle.editAnswerBtn}
                        onClick={() => {
                          setIconPickerTarget({ type: 'option', path, optionIndex: idx });
                          setShowIconPicker(true);
                        }}
                        title="Select Answer Icon"
                        style={{ color: '#4a7feb' }}
                      >
                        {opt.icon ? <i className={opt.icon}></i> : <FaSearch size={14} />}
                      </button>
                      <button
                        className={faqStyle.editAnswerBtn}
                        onClick={() => {
                          setCurrentPathForOption(path);
                          setEditingOptionIndex(idx);
                          setOptionText(opt.text);
                          setOptionDescription(opt.description || "");
                          setOptionIcon(opt.icon || "");
                          setShowRichEditor(true);
                        }}
                        title="Edit answer"
                      >
                        <FaEdit />
                      </button>
                      <button
                        className={faqStyle.editAnswerBtn}
                        onClick={() => removeOption(path, idx)}
                        title="Remove answer"
                        style={{ color: "#dc3545" }}
                      >
                        <RiDeleteBin6Line />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {isField && (
              <div className={faqStyle.fieldProperties}>
                <div className={faqStyle.sectionTitle}>
                  ⚙️ Field Configuration:
                </div>

                <div className={faqStyle.inputTypeSection}>
                  <label>Library Component:</label>
                  {node.component_id ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          fontSize: 12,
                          background: "#eef3ff",
                          color: "#4a7feb",
                          padding: "4px 10px",
                          borderRadius: 10,
                          fontWeight: 500,
                        }}
                        title={`component_id: ${node.component_id}`}
                      >
                        📦 {attachedComponent?.name || "(missing component)"}
                      </span>

                      <button
                        className={faqStyle.actionBtn}
                        onClick={() => {
                          const detach = (cur, p) => {
                            if (!(cur instanceof Question)) {
                              Object.setPrototypeOf(cur, Question.prototype);
                            }
                            if (p.length === 0) {
                              cur.component_id = null;
                              cur.pinned = false;
                              cur.pinned_snapshot = null;
                              return;
                            }
                            const [nextIndex, ...restPath] = p;
                            detach(cur.subQuestions[nextIndex], restPath);
                          };
                          const newTree = Question.fromJSON(tree.toJSON());
                          detach(newTree, path);
                          setTree(newTree);
                        }}
                      >
                        Detach
                      </button>

                      <button
                        className={faqStyle.actionBtn}
                        onClick={() => {
                          const reimport = (cur, p) => {
                            if (!(cur instanceof Question)) {
                              Object.setPrototypeOf(cur, Question.prototype);
                            }
                            if (p.length === 0) {
                              const comp = library.find(
                                (c) => c.component_id === cur.component_id,
                              );
                              if (!comp) return;
                              cur.fieldType = comp.type || "text";
                              cur.options = Array.isArray(comp.options)
                                ? comp.options.map((o) => ({
                                  value: o.value,
                                  label: o.label,
                                }))
                                : [];
                              cur.min = comp.min ?? "";
                              cur.max = comp.max ?? "";
                              cur.step = comp.step ?? "";
                              cur.pinned = false;
                              cur.pinned_snapshot = null;
                              return;
                            }
                            const [nextIndex, ...restPath] = p;
                            reimport(cur.subQuestions[nextIndex], restPath);
                          };
                          const newTree = Question.fromJSON(tree.toJSON());
                          reimport(newTree, path);
                          setTree(newTree);
                        }}
                        title="Pull the latest definition from the library"
                      >
                        Re-import
                      </button>

                      <button
                        className={faqStyle.actionBtn}
                        onClick={() =>
                          requestPinToggle(path, node, attachedComponent)
                        }
                        title={
                          node.pinned
                            ? "This field is pinned — click to resume live reference"
                            : "Pin this field to freeze the current library values"
                        }
                        style={
                          node.pinned
                            ? {
                              background: "#fef3c7",
                              border: "1px solid #f59e0b",
                              color: "#92400e",
                            }
                            : undefined
                        }
                      >
                        {node.pinned ? "🔒 Pinned" : "🔓 Pin"}
                      </button>

                      {node.pinned && node.pinned_snapshot && (
                        <div
                          style={{
                            marginTop: 6,
                            fontSize: 11,
                            color: "#92400e",
                            background: "#fffbeb",
                            border: "1px solid #fde68a",
                            borderRadius: 6,
                            padding: "6px 10px",
                            lineHeight: 1.5,
                            width: "100%",
                          }}
                        >
                          <strong>Frozen:</strong>{" "}
                          type <code>{node.pinned_snapshot.fieldType}</code>
                          {Array.isArray(node.pinned_snapshot.options) &&
                            node.pinned_snapshot.options.length > 0 && (
                              <>
                                {" · "}
                                {node.pinned_snapshot.options.length} option
                                {node.pinned_snapshot.options.length > 1
                                  ? "s"
                                  : ""}
                              </>
                            )}
                          {node.pinned_snapshot.min !== "" &&
                            node.pinned_snapshot.min !== undefined && (
                              <>
                                {" · "}min{" "}
                                {String(node.pinned_snapshot.min)}
                              </>
                            )}
                          {node.pinned_snapshot.max !== "" &&
                            node.pinned_snapshot.max !== undefined && (
                              <>
                                {" · "}max{" "}
                                {String(node.pinned_snapshot.max)}
                              </>
                            )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <button
                      className={faqStyle.actionBtn}
                      onClick={() => {
                        setPickerTargetPath(path);
                        setPickerMode("attach-to-existing");
                        setShowPicker(true);
                      }}
                    >
                      <MdOutlineWidgets style={{ marginRight: 6 }} />
                      Import from Library
                    </button>
                  )}
                </div>

                <div className={faqStyle.inputTypeSection}>
                  <label>Field Type:</label>
                  <select
                    value={effectiveFieldType}
                    className={faqStyle.input_selectors}
                    onChange={(e) => updateFieldType(path, e.target.value)}
                    disabled={!!node.component_id || node.pinned === true}
                    title={
                      node.pinned
                        ? "Type is frozen by this field's pin snapshot. Unpin to edit."
                        : node.component_id
                          ? "Type is controlled by the imported library component. Detach to edit."
                          : ""
                    }
                  >
                    <option value="text">📝 Text</option>
                    <option value="email">📧 Email</option>
                    <option value="tel">📞 Phone</option>
                    <option value="number">🔢 Number</option>
                    <option value="textarea">📄 Text Area</option>
                    <option value="date">📅 Date</option>
                    <option value="time">⏰ Time</option>
                    <option value="datetime-local">📅⏰ Date & Time</option>
                    <option value="range">🎚️ Range Slider</option>
                    <option value="toggle">🔘 Toggle (Yes/No)</option>
                    <option value="select">📋 Dropdown</option>
                    <option value="radio">🔘 Radio Buttons</option>
                    <option value="checkbox">✅ Checkboxes</option>
                  </select>
                </div>

                <div className={faqStyle.fieldRow}>
                  <div className={faqStyle.fieldInput}>
                    <label>Placeholder:</label>
                    <input
                      type="text"
                      value={node.placeholder}
                      onChange={(e) => updatePlaceholder(path, e.target.value)}
                      placeholder="Enter placeholder text"
                      className={faqStyle.textInput}
                    />
                  </div>
                  <div className={faqStyle.fieldCheckbox}>
                    <label>
                      <input
                        type="checkbox"
                        checked={node.required}
                        onChange={(e) => updateRequired(path, e.target.checked)}
                      />
                      ⭐ Required Field
                    </label>
                  </div>
                </div>

                {effectiveFieldType === "range" && (
                  <div className={faqStyle.rangeProperties}>
                    <div className={faqStyle.rangeInputs}>
                      <div className={faqStyle.rangeInput}>
                        <label>Min Value:</label>
                        <input
                          type="number"
                          value={node.min}
                          onChange={(e) => updateMin(path, e.target.value)}
                          placeholder="0"
                          className={faqStyle.textInput}
                          disabled={
                            !!node.component_id || node.pinned === true
                          }
                        />
                      </div>
                      <div className={faqStyle.rangeInput}>
                        <label>Max Value:</label>
                        <input
                          type="number"
                          value={node.max}
                          onChange={(e) => updateMax(path, e.target.value)}
                          placeholder="100"
                          className={faqStyle.textInput}
                          disabled={
                            !!node.component_id || node.pinned === true
                          }
                        />
                      </div>
                      <div className={faqStyle.rangeInput}>
                        <label>Step:</label>
                        <input
                          type="number"
                          value={node.step}
                          onChange={(e) => updateStep(path, e.target.value)}
                          placeholder="1"
                          className={faqStyle.textInput}
                          disabled={
                            !!node.component_id || node.pinned === true
                          }
                        />
                      </div>
                    </div>
                  </div>
                )}

                {effectiveFieldType === "toggle" && (
                  <div className={faqStyle.toggleProperties}>
                    <div className={faqStyle.toggleInput}>
                      <label>Default Value:</label>
                      <select
                        value={node.defaultValue}
                        onChange={(e) =>
                          updateDefaultValue(path, e.target.value)
                        }
                        className={faqStyle.input_selectors}
                      >
                        <option value="false">No (Default)</option>
                        <option value="true">Yes</option>
                      </select>
                    </div>
                  </div>
                )}

                {["select", "radio", "checkbox"].includes(
                  effectiveFieldType,
                ) && (
                    <div className={faqStyle.optionsSection}>
                      <div className={faqStyle.optionsHeader}>
                        <label>
                          {effectiveFieldType === "select"
                            ? "📋 Dropdown Options:"
                            : effectiveFieldType === "radio"
                              ? "🔘 Radio Options:"
                              : "✅ Checkbox Options:"}
                        </label>
                        <button
                          className={faqStyle.optionsButton}
                          onClick={() => handleOpenOptionsModal(path, node)}
                          disabled={!!node.component_id || node.pinned === true}
                          title={
                            node.pinned
                              ? "Options are frozen by this field's pin snapshot. Unpin to edit."
                              : node.component_id
                                ? "Options are controlled by the imported library component. Detach to edit."
                                : ""
                          }
                        >
                          {effectiveOptions?.length > 0 ? (
                            <>
                              <BiSolidEdit className={faqStyle.icon} /> Edit
                            </>
                          ) : (
                            <>
                              <FaPlus className={faqStyle.icon} /> Add
                            </>
                          )}
                        </button>
                      </div>
                      {effectiveOptions?.length > 0 && (
                        <div className={faqStyle.optionsPreview}>
                          <strong>Current Options:</strong>
                          <div className={faqStyle.optionsList}>
                            {effectiveOptions.map((option, idx) => (
                              <span key={idx} className={faqStyle.optionTag}>
                                {typeof option === "object"
                                  ? option.label
                                  : option}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
              </div>
            )}

            {isForm && (
              <div
                style={{
                  marginTop: 12,
                  marginBottom: 8,
                  border: "1px solid #e3e8f0",
                  borderRadius: 8,
                  background: "#fafbff",
                  overflow: "hidden",
                }}
              >
                <button
                  onClick={() => toggleActionsDropdown(pathKey)}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    padding: "10px 14px",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    fontWeight: 600,
                    color: "#3a3f52",
                    fontSize: 14,
                  }}
                  title="Show / hide form validation rules & parallel triggers"
                >
                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <FaBolt style={{ color: "#4a7feb" }} />
                    Validation & Triggers
                    {(triggerCount > 0 || validationCount > 0) && (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 500,
                          background: "#eef3ff",
                          color: "#3a6fd4",
                          borderRadius: 10,
                          padding: "2px 8px",
                        }}
                      >
                        {validationCount > 0
                          ? `${validationCount} rule${validationCount > 1 ? "s" : ""}`
                          : ""}
                        {validationCount > 0 && triggerCount > 0 ? " · " : ""}
                        {triggerCount > 0
                          ? `${triggerCount} trigger${triggerCount > 1 ? "s" : ""}`
                          : ""}
                      </span>
                    )}
                  </span>
                  <span style={{ color: "#8a93a6" }}>
                    {isActionsOpen ? <FaChevronDown /> : <FaChevronRight />}
                  </span>
                </button>

                {isActionsOpen && (
                  <div
                    style={{
                      padding: "0 14px 14px 14px",
                      borderTop: "1px solid #e3e8f0",
                    }}
                  >
                    <div style={{ marginTop: 10 }}>
                      <ValidationRulesBuilder
                        rules={validationRules}
                        availableFields={(node?.subQuestions || [])
                          .filter((q) => q.type === "field_type")
                          .map((q) => ({
                            field:
                              q.field ||
                              q.text.toLowerCase().replace(/\s+/g, "_"),
                            label: q.text,
                          }))}
                        onChange={handleRulesChange}
                        loading={rulesLoading}
                      />
                    </div>

                    <div style={{ marginTop: 14 }}>
                      <TriggerAttachmentList
                        attachments={node.triggers || []}
                        definitionsById={Object.fromEntries(
                          (connectorDefs || []).map((d) => [d.definition_id, d]),
                        )}
                        siteFields={siteFieldsFromFormNode(node)}
                        operations={operations}
                        onChange={(next) => {
                          const newTree = Question.fromJSON(tree.toJSON());
                          const mutate = (cur, p) => {
                            if (!(cur instanceof Question)) {
                              Object.setPrototypeOf(cur, Question.prototype);
                            }
                            if (p.length === 0) {
                              cur.triggers = next;
                              return;
                            }
                            const [nextIndex, ...restPath] = p;
                            if (cur.subQuestions?.[nextIndex]) {
                              mutate(cur.subQuestions[nextIndex], restPath);
                            }
                          };
                          mutate(newTree, path);
                          setTree(newTree);
                        }}
                        onAdd={() => openTriggerAttachFlow(path)}
                        readOnly={false}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Action Row for MCQ / Leaf nodes (no Remove button here anymore) */}
            {!isForm && (
              <div className={faqStyle.actionRow} style={{ gap: 12, flexWrap: "wrap" }}>
                {showAddQuestionButton && (
                  <AddQuestionButton
                    path={path}
                    addNode={addNode}
                    faqStyle={faqStyle}
                    parentType={node.type}
                    onImportFromLibrary={(fieldPath) => {
                      setPickerTargetPath(fieldPath);
                      setPickerMode("create-new");
                      setShowPicker(true);
                    }}
                  />
                )}

                {!isField && !isRoot && (
                  <button
                    className={`${faqStyle.actionBtn} ${faqStyle.addStat}`}
                    onClick={() => addStatement(path)}
                    style={{ flex: "1 1 auto", minWidth: "150px" }}
                  >
                    <MdOutlineAddToPhotos style={{ marginRight: "6px" }} />
                    Add Description
                  </button>
                )}

                {showAddOptionButton && (
                  <button
                    className={`${faqStyle.actionBtn} ${faqStyle.addAns}`}
                    onClick={() => {
                      setCurrentPathForOption(path);
                      setEditingOptionIndex(-1);
                      setOptionText("");
                      setOptionDescription("");
                      setOptionIcon("");
                      setShowRichEditor(true);
                    }}
                    style={{ flex: "1 1 auto", minWidth: "150px" }}
                  >
                    <MdOutlineAddToPhotos
                      style={{ marginRight: "6px" }}
                      size={22}
                    />
                    ✨ Add Answer
                  </button>
                )}
              </div>
            )}

            <div className={faqStyle.subQuestions}>
              {node.subQuestions.map((childNode, index) =>
                renderTree(childNode, [...path, index], node.type),
              )}
            </div>

            {/* BOTTOM Add Field button (for forms) */}
            {isForm && (
              <div style={{ marginTop: 10 }}>
                <button
                  className={`${faqStyle.actionBtn} ${faqStyle.addQst}`}
                  onClick={() => {
                    const newQuestionText = prompt("Enter a new field:");
                    if (!newQuestionText) return;
                    const newField = new Question(newQuestionText, "", "field_type");
                    newField.field = newQuestionText.toLowerCase().replace(/\s+/g, "_");
                    addNode(path, newField);
                  }}
                  style={{ width: "100%" }}
                >
                  <MdOutlineAddToPhotos style={{ marginRight: "6px" }} size={22} />
                  Add Field
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const attachFlowSiteFields = useMemo(() => {
    if (!tree || !Array.isArray(attachFlowTargetPath)) return [];
    const target = getNodeForPath(tree, attachFlowTargetPath);
    if (!target) return [];
    return siteFieldsFromFormNode(target);
  }, [tree, attachFlowTargetPath]);

  return (
    <div className={faqStyle.faq_outerWrap}>
      <EditQuestionModal
        isOpen={showEditQuestionModal}
        onClose={() => setShowEditQuestionModal(false)}
        onSave={(newText) => updateNodeText(currentEditPath, newText)}
        initialText={editInitialText}
      />

      <EditStatementModal
        isOpen={showEditStatementModal}
        onClose={() => setShowEditStatementModal(false)}
        onSave={(newStatement) =>
          updateStatement(currentStatementPath, newStatement)
        }
        initialStatement={editInitialStatement}
      />

      <PreviewModal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        node={previewNode}
      />

      {showIconPicker && (
        <FontAwesomeIconPicker
          onSelect={(icon) => {
            if (iconPickerTarget) {
              updateIcon(iconPickerTarget, icon);
            }
          }}
          onClose={() => setShowIconPicker(false)}
        />
      )}

      {showRichEditor && (
        <div className={faqStyle.modalOverlay} style={{ zIndex: 9999 }}>
          <div className={faqStyle.modal} style={{ width: "80%", maxWidth: "800px" }}>
            <SpecialRichEditor
              isOpen={showRichEditor}
              onClose={() => {
                setShowRichEditor(false);
                setEditingOptionIndex(-1);
              }}
              answerText={optionText}
              setAnswerText={setOptionText}
              answerDescription={optionDescription}
              setAnswerDescription={setOptionDescription}
              onSave={handleSaveOption}
            />
          </div>
        </div>
      )}

      {showOptionsModal && (
        <div className={faqStyle.modalOverlay}>
          <div className={faqStyle.modal}>
            <h3 className={faqStyle.modalTitle}>Manage Options</h3>
            <div className={faqStyle.modalContent}>
              <p>Enter options separated by commas:</p>
              <textarea
                value={optionsInput}
                onChange={(e) => setOptionsInput(e.target.value)}
                placeholder="Option 1, Option 2, Option 3"
                rows={4}
                className={faqStyle.optionsTextarea}
              />
              <div className={faqStyle.optionsExample}>
                <strong>Example:</strong> "Small, Medium, Large" or "Red, Green,
                Blue"
              </div>
            </div>
            <div className={faqStyle.modalActions}>
              <button
                className={faqStyle.modalCancel}
                onClick={() => setShowOptionsModal(false)}
              >
                Cancel
              </button>
              <button
                className={faqStyle.modalConfirm}
                onClick={handleSaveOptions}
              >
                Save Options
              </button>
            </div>
          </div>
        </div>
      )}

      <TriggerAttachFlow
        isOpen={attachFlowOpen}
        onClose={closeTriggerAttachFlow}
        siteFields={attachFlowSiteFields}
        connectedProviders={connectedProviders}
        definitions={connectorDefs}
        operations={operations}
        existingAttachments={
          attachFlowTargetPath && tree
            ? getNodeForPath(tree, attachFlowTargetPath)?.triggers || []
            : []
        }
        onAttach={handleAttachTrigger}
      />

      <ComponentPickerModal
        isOpen={showPicker}
        onClose={() => {
          setShowPicker(false);
          setPickerMode("attach-to-existing");
        }}
        library={library}
        loading={libraryLoading}
        onSelect={handlePickerSelect}
      />

      <PinConfirmModal
        isOpen={!!pinModalTarget}
        onClose={() => setPinModalTarget(null)}
        onConfirm={() => applyPinDecision(true)}
        mode={pinModalMode}
        fieldLabel={pinModalTarget?.fieldLabel || ""}
        componentName={pinModalTarget?.componentName || ""}
      />

      <div className={faqStyle.faqLeft}>
        {!tree ? (
          <div className={faqStyle.rootButtons}>
            <div className={faqStyle.rootButtonContainer}>
              <button
                onClick={() => {
                  const rootQuestionText = prompt(
                    "Enter the root message (welcome message):",
                  );
                  if (!rootQuestionText) return;
                  setTree(
                    new Question(rootQuestionText, "", "Multiple Choice"),
                  );
                }}
                className={faqStyle.rootstyle}
              >
                🌳 Add Root of the Tree
              </button>
              <span className={faqStyle.helperText}>
                <FaInfoCircle /> Usually a welcome message as it's shown very
                first
              </span>
            </div>
            <div className={faqStyle.loadJson}>
              <button
                onClick={() => setShowLoadModal(true)}
                className={faqStyle.rootstyle}
              >
                📂 Load from Custom JSON (Q-MC/Q-UI)
              </button>
              <button
                onClick={() => setShowRawLoadModal(true)}
                className={faqStyle.rootstyle}
              >
                📄 Load from Raw JSON
              </button>
            </div>
          </div>
        ) : showJSON ? (
          <pre className={faqStyle.jsonView}>
            {JSON.stringify(
              useCustomJSON ? tree.toCustomJSON() : tree.toJSON(),
              null,
              2,
            )}
          </pre>
        ) : (
          renderTree(tree, [], null)
        )}
      </div>

      {showRawLoadModal && (
        <div className={faqStyle.modalOverlay}>
          <div className={faqStyle.modal}>
            <h3 className={faqStyle.modalTitle}>Load from Raw JSON</h3>
            <textarea
              value={rawJSONInput}
              onChange={(e) => setRawJSONInput(e.target.value)}
              placeholder={`Paste your raw JSON here...`}
              rows={12}
              style={{
                width: "100%",
                fontFamily: "monospace",
                fontSize: "12px",
              }}
            />
            <div className={faqStyle.modalActions}>
              <button
                className={faqStyle.modalCancel}
                onClick={() => setShowRawLoadModal(false)}
              >
                Cancel
              </button>
              <button
                className={faqStyle.modalConfirm}
                onClick={handleLoadFromRawJSON}
              >
                Load Raw JSON
              </button>
            </div>
          </div>
        </div>
      )}

      {showLoadModal && (
        <div className={faqStyle.modalOverlay}>
          <div className={faqStyle.modal}>
            <h3 className={faqStyle.modalTitle}>Load from Custom JSON</h3>
            <textarea
              value={customJSONInput}
              onChange={(e) => setCustomJSONInput(e.target.value)}
              placeholder={`Paste your custom JSON here...`}
              rows={10}
              style={{
                width: "100%",
                fontFamily: "monospace",
                fontSize: "12px",
              }}
            />
            <div className={faqStyle.modalActions}>
              <button
                className={faqStyle.modalCancel}
                onClick={() => setShowLoadModal(false)}
              >
                Cancel
              </button>
              <button
                className={faqStyle.modalConfirm}
                onClick={handleLoadFromJSON}
              >
                Load Custom JSON
              </button>
            </div>
          </div>
        </div>
      )}

      {tree && (
        <div className={faqStyle.toggleButtons}>
          <button onClick={() => setShowJSON((prev) => !prev)}>
            {showJSON ? "Hide JSON View" : "View JSON"}
          </button>
          {showJSON && (
            <button onClick={() => setUseCustomJSON((prev) => !prev)}>
              {useCustomJSON
                ? "Switch to Raw JSON"
                : "Switch to Custom JSON (Q-MC/Q-UI/O)"}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default MyQNATest;